import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigation } from '@/contexts/NavigationContext';
import { useTeams } from '@/hooks/useTeams';
import { useCreatePrompt, useUpdatePrompt, useDeletePrompt } from '@/hooks/usePrompts';
import { useResourcePermissions } from '@/hooks/usePermissions';
import { useProfile } from '@/hooks/useProfile';
import { toast } from 'sonner';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { ContextBudgetIndicator } from '@/components/ContextBudgetIndicator';
import { ResourceAccessSection } from '@/components/ResourceAccessSection';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { Tables } from '@/integrations/supabase/types';

const CATEGORIES = ['Marketing', 'Vendas', 'Desenvolvimento', 'RH', 'Jurídico', 'Geral'];

const promptSchema = z.object({
  name: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(100),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  category: z.string().trim().max(50).optional().or(z.literal('')),
  content: z.string().trim().min(1, 'O conteúdo do prompt é obrigatório').max(500000),
  scopeValue: z.string().min(1, 'Selecione um escopo'),
});

type PromptFormValues = z.infer<typeof promptSchema>;

function parseScopeValue(value: string) {
  if (value === 'global') return { scope_type: 'global', scope_id: null, project_id: null };
  if (value === 'personal') return { scope_type: 'personal', scope_id: null, project_id: null };
  if (value.startsWith('team:')) return { scope_type: 'team', scope_id: value.slice(5), project_id: null };
  if (value.startsWith('project:')) return { scope_type: 'global', scope_id: null, project_id: value.slice(8) };
  return { scope_type: 'global', scope_id: null, project_id: null };
}

function buildScopeValue(prompt: Tables<'prompts'>): string {
  if (prompt.project_id) return `project:${prompt.project_id}`;
  if (prompt.scope_type === 'team' && prompt.scope_id) return `team:${prompt.scope_id}`;
  if (prompt.scope_type === 'personal') return 'personal';
  return 'global';
}

export const PromptFormPage = () => {
  const { user } = useAuth();
  const { navigation, editingResource, setEditingResource } = useNavigation();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { isAdmin } = useProfile();
  const { data: teams } = useTeams();
  const createPrompt = useCreatePrompt();
  const updatePrompt = useUpdatePrompt();
  const deletePrompt = useDeletePrompt();

  const promptId = editingResource?.id;
  const isEditing = !!promptId;

  const { data: prompt } = useQuery({
    queryKey: ['prompt-detail', promptId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .eq('id', promptId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!promptId,
  });
  const { canEdit: canManagePromptAccess } = useResourcePermissions(prompt?.owner_id ?? '', prompt?.project_id ?? undefined);

  const defaultScopeValue = () => {
    if (navigation.projectId) return `project:${navigation.projectId}`;
    if (navigation.isPersonal) return 'personal';
    return 'global';
  };

  const form = useForm<PromptFormValues>({
    resolver: zodResolver(promptSchema),
    defaultValues: {
      name: '',
      description: '',
      category: 'Geral',
      content: '',
      scopeValue: defaultScopeValue(),
    },
  });

  useEffect(() => {
    if (prompt) {
      form.reset({
        name: prompt.name,
        description: prompt.description ?? '',
        category: prompt.category ?? 'Geral',
        content: prompt.content,
        scopeValue: buildScopeValue(prompt),
      });
    }
  }, [prompt]);

  const onSubmit = async (values: PromptFormValues) => {
    const { scope_type, scope_id, project_id } = parseScopeValue(values.scopeValue);

    try {
      if (isEditing && promptId) {
        await updatePrompt.mutateAsync({
          id: promptId,
          name: values.name,
          description: values.description || null,
          category: values.category || null,
          content: values.content,
          scope_type,
          scope_id,
          project_id,
        });
        toast.success('Prompt atualizado');
      } else {
        await createPrompt.mutateAsync({
          name: values.name,
          description: values.description || null,
          category: values.category || null,
          content: values.content,
          scope_type,
          scope_id,
          project_id,
        });
        toast.success('Prompt criado com sucesso');
      }
      setEditingResource(null);
    } catch (err) {
      toast.error(`Erro ao salvar prompt: ${(err as Error).message}`);
    }
  };

  const handleDelete = async () => {
    if (!promptId) return;
    try {
      await deletePrompt.mutateAsync(promptId);
      toast.success('Prompt excluído');
      setEditingResource(null);
    } catch (err) {
      toast.error(`Erro ao excluir prompt: ${(err as Error).message}`);
    }
  };

  const isPending = createPrompt.isPending || updatePrompt.isPending || deletePrompt.isPending;

  return (
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-color">
        <button
          onClick={() => setEditingResource(null)}
          className="p-1.5 rounded-md text-tertiary hover:text-primary hover:bg-hover transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-primary">
          {isEditing ? 'Editar Prompt' : 'Novo Prompt'}
        </h1>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-4xl mx-auto space-y-6">
          {/* Nome + Categoria */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">Nome *</label>
              <input
                {...form.register('name')}
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Nome do prompt"
              />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive mt-1">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">Categoria</label>
              <input
                {...form.register('category')}
                list="prompt-categories"
                className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                placeholder="Selecione ou digite"
              />
              <datalist id="prompt-categories">
                {CATEGORIES.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Descrição</label>
            <textarea
              {...form.register('description')}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none"
              placeholder="Descrição opcional"
            />
          </div>

          {/* Escopo */}
          <div className="max-w-xs">
            <label className="block text-sm font-medium text-primary mb-1.5">Ambiente *</label>
            <select
              {...form.register('scopeValue')}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="global">🏢 Espaço Caldeira</option>
              {teams?.map(team => (
                <option key={team.id} value={`team:${team.id}`}>{team.icon} Time — {team.name}</option>
              ))}
              <option value="personal">👤 Pessoal</option>
              {navigation.projectId && (
                <option value={`project:${navigation.projectId}`}>📁 Projeto atual</option>
              )}
            </select>
            {form.formState.errors.scopeValue && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.scopeValue.message}</p>
            )}
          </div>

          {isEditing && prompt && (
            <ResourceAccessSection
              type="prompt"
              resourceId={prompt.id}
              ownerId={prompt.owner_id}
              scopeType={prompt.scope_type}
              projectId={prompt.project_id}
              canManage={isAdmin || canManagePromptAccess}
            />
          )}

          {/* Conteúdo */}
          <div>
            <label className="block text-sm font-medium text-primary mb-1.5">Conteúdo do Prompt *</label>
            <textarea
              {...form.register('content')}
              rows={20}
              className="w-full px-3 py-2 rounded-md border border-color bg-surface text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-primary resize-y font-mono text-xs leading-relaxed"
              placeholder="Digite o conteúdo do prompt aqui..."
            />
            <ContextBudgetIndicator value={form.watch('content') ?? ''} />
            {form.formState.errors.content && (
              <p className="text-xs text-destructive mt-1">{form.formState.errors.content.message}</p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3 pb-6">
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={isPending}
                className="px-6 py-2.5 brand-primary rounded-md text-sm font-medium disabled:opacity-50"
              >
                {isPending ? 'Salvando...' : isEditing ? 'Salvar Alterações' : 'Criar Prompt'}
              </button>
              <button
                type="button"
                onClick={() => setEditingResource(null)}
                className="px-6 py-2.5 border border-color rounded-md text-sm font-medium text-primary hover:bg-hover"
              >
                Cancelar
              </button>
            </div>

            {isEditing && isAdmin && (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                disabled={isPending}
                className="px-4 py-2.5 border border-destructive text-destructive rounded-md text-sm font-medium hover:bg-destructive/10 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Excluir prompt
              </button>
            )}
          </div>
        </form>
      </div>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir prompt?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação é permanente. O prompt “{prompt?.name ?? 'selecionado'}” será removido da biblioteca.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
